import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
  useTheme,
} from '@mui/material';
import {
  PersonAddAltIcon,
  SettingsSharpIcon,
  StorageSharpIcon,
  TagIcon,
} from 'assets/icons';
import { NablaLogo } from 'assets/images';
import { Alert } from 'components/commons';
import DialogPrivateDB from 'components/features/SettingDB/DialogPrivateDB';
import DialogSettingDB from 'components/features/SettingDB/DialogSettingDB';
import { PAGE_LIST } from 'constant/pages';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import {
  getKeyFromLocalStorage,
  removeKeyFromLocalStorage,
  saveKeyToLocalStorage,
} from 'utils/constant';
import { postAuthLogin } from 'utils/fetch';
import ja from 'utils/ja';

export const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export interface ChangeDBFormType {
  dbId: string;
  dbKey: string;
}

const CHANGE_DB_FORM_DEFAULT_VALUE = {
  dbId: '',
  dbKey: '',
};

interface ISidebarProps {
  open: boolean;
  handleClose: () => void;
}

const SideBar: React.FC<ISidebarProps> = ({ open, handleClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [openDialogSettingDb, setOpenDialogSettingDb] =
    useState<boolean>(false);
  const [openDialogPrivateDb, setOpenDialogPrivateDb] =
    useState<boolean>(false);
  const [configError, setConfigError] = useState<boolean>(false);

  const {
    control,
    watch,
    reset,
    trigger,
    formState: { isValid },
  } = useForm<ChangeDBFormType>({
    defaultValues: CHANGE_DB_FORM_DEFAULT_VALUE,
  });

  const { dbId, dbKey } = watch();

  const handleConfimSettingDb = () => {
    removeKeyFromLocalStorage(['appId', 'appKey', 'dbId', 'dbKey', 'url']);
    setOpenDialogSettingDb(false);
    navigate(PAGE_LIST.SETTING.pathname());
  };

  const handleConfimPrivateDb = async () => {
    trigger();
    if (isValid) {
      setOpenDialogPrivateDb(false);
      try {
        const appId = getKeyFromLocalStorage('appId');
        const appKey = getKeyFromLocalStorage('appKey');

        await postAuthLogin({
          appId,
          appKey,
          dbId,
          dbKey,
        });

        removeKeyFromLocalStorage(['dbId', 'dbKey']);

        saveKeyToLocalStorage('dbId', dbId);
        saveKeyToLocalStorage('dbKey', dbKey);

        const currentPath = location.pathname;
        const isHome = matchPath(PAGE_LIST.HOME.pathname(), currentPath);
        if (isHome) {
          navigate(0);
        } else {
          navigate(PAGE_LIST.HOME.pathname());
          window.location.reload();
        }
      } catch (error) {
        reset({
          dbId: getKeyFromLocalStorage('dbId') ?? '',
          dbKey: getKeyFromLocalStorage('dbKey') ?? '',
        });
        setConfigError(true);
      }
    }
  };

  useEffect(() => {
    reset({
      dbId: getKeyFromLocalStorage('dbId') ?? '',
      dbKey: getKeyFromLocalStorage('dbKey') ?? '',
    });
  }, []);

  const handleCloseDialogPrivateDb = () => {
    setOpenDialogPrivateDb(false);
  };

  const sidebarItems = [
    {
      icon: (
        <PersonAddAltIcon
          sx={{ fontSize: { xs: '40px', sm: '60px' }, fill: '#F3C623' }}
        />
      ),
      label: ja.side_bar.user,
      onClick: () => {
        handleClose();
        navigate(PAGE_LIST.HOME.pathname());
      },
    },
    {
      icon: (
        <TagIcon
          sx={{ fontSize: { xs: '40px', sm: '60px' }, fill: '#06923E' }}
        />
      ),
      label: ja.side_bar.tag,
      onClick: () => {
        handleClose();
        navigate(PAGE_LIST.TAG.pathname());
      },
    },
    {
      icon: (
        <StorageSharpIcon
          sx={{ fontSize: { xs: '40px', sm: '60px' }, fill: '#1A96F0' }}
        />
      ),
      label: ja.side_bar.database,
      onClick: () => setOpenDialogPrivateDb(true),
    },
    {
      icon: (
        <SettingsSharpIcon
          sx={{ fontSize: { xs: '40px', sm: '60px' }, fill: '#FF0000' }}
        />
      ),
      label: ja.side_bar.db_setting,
      onClick: () => setOpenDialogSettingDb(true),
    },
  ];

  return (
    <Drawer
      sx={{
        width: { xs: '90%', sm: '435px' },
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: { xs: '90%', sm: '435px' },
          boxSizing: 'border-box',
        },
      }}
      variant='temporary'
      anchor='left'
      open={open}
      onClose={handleClose}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', py: '35px' }}>
        <Box
          component='img'
          src={NablaLogo}
          alt='Logo'
          sx={{ width: { xs: '254px', sm: '314px' } }}
        />
      </Box>
      <List>
        {sidebarItems.map(({ label, icon, onClick }, index) => (
          <ListItem key={`sidebar-items-${index}`} disablePadding>
            <ListItemButton
              onClick={onClick}
              sx={{
                border: `1px solid`,
                borderColor: theme.palette.primary.main,
                borderRadius: '8px',
                boxShadow: '0px 4px 4px 0px #00000040',
                mx: '10px',
                mb: '15px',
              }}
            >
              <ListItemIcon> {icon}</ListItemIcon>
              <ListItemText
                primary={label}
                sx={{
                  textAlign: 'right',
                  '& .MuiTypography-root': {
                    fontSize: { xs: '18px', sm: '30px' },
                    fontWeight: '700',
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <DialogSettingDB
        open={openDialogSettingDb}
        onClose={() => setOpenDialogSettingDb(false)}
        handleConfirm={handleConfimSettingDb}
      />
      <DialogPrivateDB
        open={openDialogPrivateDb}
        onClose={handleCloseDialogPrivateDb}
        control={control}
        handleConfirm={handleConfimPrivateDb}
      />
      <Alert
        open={configError}
        onClose={() => setConfigError(false)}
        message={ja.message_error.app_config}
        type='error'
      />
    </Drawer>
  );
};

export default SideBar;
